/**
* =========================================================================================
* Component - Popup
* =========================================================================================
**/

import React, { useEffect, useRef, useMemo } from 'react';
import Popup from 'reactjs-popup';
import { isInApp } from '../common/function';
import PropTypes from 'prop-types';

const UPopup = (props) => {

    const popupRef = useRef(null);

    useEffect(() => {
        const layout = document.querySelector('.u_layout');
        if (layout) {
            layout.addEventListener('wheel', handleWheel, { passive: true });
        }
        return () => {
            if (layout) {
                layout.removeEventListener('wheel', handleWheel);
            }
        };
    }, []);

    const handleWheel = (e) => {
        if(popupRef?.current) popupRef?.current?.close();
    }

    useEffect(() => {
        if(!props?.open) {
            popupRef?.current?.close();
        }
        props?.close?.();
    }, [props?.open]);

    // Close popup on inner child click
    useEffect(() => {
        if (popupRef.current) popupRef.current.close();
        props?.closeMenuOpen?.();
    }, [props?.closeMenu]);

    // Robust style resolver for content and overlay styles
    const resolvedContentStyle = useMemo(() => {
        const defaultZIndex = 10001;
        let base = {};
        if (typeof props?.contentStyle === 'object' && props?.contentStyle !== null) {
            base = { ...props.contentStyle };
        } else if (typeof props?.contentStyle === 'number' || typeof props?.contentStyle === 'string') {
            base = { zIndex: props.contentStyle };
        } else {
            base = { zIndex: defaultZIndex };
        }
        return base;
    }, [props?.contentStyle]);

    const resolvedOverlayStyle = useMemo(() => {
        const defaultZIndex = 10001;
        let base = {};
        if (typeof props?.overlayStyle === 'object' && props?.overlayStyle !== null) {
            base = { ...props.overlayStyle };
        } else if (typeof props?.overlayStyle === 'number' || typeof props?.overlayStyle === 'string') {
            base = { zIndex: props.overlayStyle };
        } else if (typeof props?.contentStyle === 'number' || typeof props?.contentStyle === 'string') {
            base = { zIndex: props.contentStyle };
        } else {
            base = { zIndex: defaultZIndex };
        }
        return base;
    }, [props?.overlayStyle, props?.contentStyle]);

    // Render description conditionally with proper element wrapping
    const renderedDesc = useMemo(() => {
        if (!props?.desc) return null;
        
        const isStringOrNumber = typeof props?.desc === 'string' || typeof props?.desc === 'number';
        
        if (isInApp() && props.inModal !== undefined) {
            return <span className="u_tooltip">{props?.desc}</span>;
        }
        if (isStringOrNumber) {
            return <span>{props?.desc}</span>;
        }
        return props?.desc;
    }, [props?.desc, props?.inModal]);

    const showModal = useMemo(() => {
        return isInApp() && props.inModal !== undefined;
    }, [props.inModal]);

    return (
        <React.Fragment>
            {showModal ? (
                <Popup
                    modal
                    ref={popupRef}
                    className="u_modal-popup u_tooltip"
                    contentStyle={resolvedContentStyle}
                    overlayStyle={resolvedOverlayStyle}
                    trigger={open => props?.children}
                    closeOnDocumentClick
                    repositionOnResize
                    keepTooltipInside={true}
                    {...props?.params}
                >
                    {renderedDesc}
                </Popup>
            ) : (
                <Popup
                    ref={popupRef}
                    trigger={open => props?.children}
                    arrow={props?.arrow !== undefined ? props.arrow : true}
                    on={['hover', 'focus']}
                    position="bottom center"
                    closeOnDocumentClick
                    repositionOnResize
                    keepTooltipInside={true}
                    contentStyle={resolvedContentStyle}
                    overlayStyle={resolvedOverlayStyle}
                    {...props?.params}
                >
                    {renderedDesc}
                </Popup>
            )}
        </React.Fragment>
    );
};

UPopup.propTypes = {
    open: PropTypes.bool,
    close: PropTypes.func,
    closeMenu: PropTypes.any,
    closeMenuOpen: PropTypes.func,
    arrow: PropTypes.bool,
    inModal: PropTypes.any,
    desc: PropTypes.node,
    contentStyle: PropTypes.oneOfType([PropTypes.object, PropTypes.number, PropTypes.string]),
    overlayStyle: PropTypes.oneOfType([PropTypes.object, PropTypes.number, PropTypes.string]),
    params: PropTypes.object,
    children: PropTypes.node,
};

UPopup.defaultProps = {
    open: true,
    close: () => {},
    closeMenu: undefined,
    closeMenuOpen: () => {},
    arrow: undefined,
    inModal: undefined,
    desc: null,
    contentStyle: undefined,
    overlayStyle: undefined,
    params: undefined,
    children: null,
};

export default UPopup;
